import React, { useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Stack, Button } from "@mui/material"
import dayjs from "dayjs"

import {
	PSE_STATUS,
	showProductionStepsSupervision,
	PSEGoOneStepBack,
	loadProductionStepExecution
} from "../../actions/ProductionSteps/ProductionStepExecutions"
import PSEProgressBar from "./preview/PSEProgressBar"
import PSEPreparation from "./preview/PSEPreparation"
import PSEWeights from "./preview/PSEWeights"
import PSECookingRecap from "./preview/PSECookingRecap"
import PSECoolingRecap from "./preview/PSECoolingRecap"
import { showProductionStepExecution } from "../../actions/ProductionSteps/ProductionStepExecutions"
import PSEStepComponents from "./preview/PSEStepComponents"
import { StyledMainContainer } from "./styledComponents"
import ProductionStepsExecutionFixedHeader from "./ProductionSteoExecutionsFixedHeader"
import ProductionStepExecutionFooter from "./ProductionStepExecutionFooter"
import { showAlerts } from "../../actions/ProductionSteps/alerts"
import { getPSESectionSelector } from "../../reducers/Production/ProductionSteps"
import PSESectionOuptutRecap from "./preview/PSESectionOuptutRecap"

const ProductionStepExecutionDone = ({
	productionStepExecution,
	isFromAlertsPage
}) => {

	if (!productionStepExecution) return null
	const pseSection = useSelector(getPSESectionSelector)

	const dispatch = useDispatch()

	const transformationType = useMemo(() => productionStepExecution.productionStepSnapshot.transformation, [productionStepExecution])

	const handleBack = () => {
		if (isFromAlertsPage) {
			dispatch(showAlerts(dayjs(productionStepExecution.stepProductionDate).format("DD-MM-YYYY")))
			return
		}
		dispatch(showProductionStepsSupervision())
	}

	const handleStepBack = async () => {
		await PSEGoOneStepBack(productionStepExecution.objectId || productionStepExecution.id)
		dispatch(loadProductionStepExecution({ id: productionStepExecution.objectId || productionStepExecution.id }))
	}

	const ulteriorStep = productionStepExecution?.ulteriorStep
	const isUlteriorStepNotStarted = ulteriorStep && ["TODO", "LOCKED"].includes(ulteriorStep.status)
	// Determines if the ulterior step is considered "direct":
	// same production date and same kitchen area.
	const isDirectNextStep = useMemo(() => {
		return (
			ulteriorStep &&
			productionStepExecution.stepProductionDate === ulteriorStep.stepProductionDate &&
			productionStepExecution.productionStepSnapshot.kitchenArea.objectId === ulteriorStep.productionStepSnapshot.kitchenArea.objectId
		)
	}, [ulteriorStep, productionStepExecution])


	return (
		<>
			{/* header */}
			<ProductionStepsExecutionFixedHeader onBack={handleBack} />

			{/* body */}
			<StyledMainContainer sx={{ paddingTop: 0 }}>
				<Stack spacing={3} sx={{ p: 3, borderBottom: "1px solid #E0E0E0" }}>
					{!productionStepExecution.isSectionLastStep && //hide because it's already hidden in TO_TEST status
						<PSEProgressBar
							currentSubStatus={productionStepExecution.subStatus}
							productionStepExecution={productionStepExecution}
							isDone={[PSE_STATUS.toTest, PSE_STATUS.done].includes(productionStepExecution.status)}
							times={{
								startTime: productionStepExecution.startTime,
								endTime: productionStepExecution.endTime,
								theoreticalStartTime: productionStepExecution.theoreticalStartTime,
								theoreticalEndTime: productionStepExecution.theoreticalEndTime
							}}
						/>}
				</Stack>
				<Stack spacing={3} sx={{ p: 3 }}>
					<PSEPreparation productionStepExecution={productionStepExecution} />
					<PSEWeights
						productionStepExecution={productionStepExecution}
						status={productionStepExecution.status}
						isVisible={productionStepExecution.productionStepSnapshot?.endStepWeighing === true}
					/>
					{productionStepExecution.isSectionLastStep && (
						(pseSection?.sectionOutputs && pseSection.sectionOutputs.length > 0) ||
						(productionStepExecution.isStepMerged && productionStepExecution.mergedProductionStepExecutions?.length > 0)
					) && <PSESectionOuptutRecap productionStepExecution={productionStepExecution} />}

					<PSECookingRecap
						visible={transformationType === "COOKING"}
						machinesBatch={productionStepExecution.machinesBatch}
						pseId={productionStepExecution.objectId}
						isFromAlertsPage={isFromAlertsPage}
					/>
					<PSECoolingRecap
						visible={transformationType === "COOLING"}
						machinesBatch={productionStepExecution.machinesBatch}
						pseId={productionStepExecution.objectId}
						isFromAlertsPage={isFromAlertsPage}
					/>
					<PSEStepComponents
						stepComponents={productionStepExecution.productionStepSnapshot.stepComponents}
						substitutes={productionStepExecution.substitutes}
						productionStepExecution={productionStepExecution}
						expectedProduction={productionStepExecution.expectedProduction}
						coeff={productionStepExecution.coeff}
						isReusableStep={!!productionStepExecution.reusableProductionStep}
						theoreticalGrossWeight={productionStepExecution.theoreticalGrossWeight}
					/>
				</Stack>
			</StyledMainContainer>
			<ProductionStepExecutionFooter
				rightAction={
					<div style={{ display: "flex", justifyContent: "flex-end", gap: "16px" }}>
						<Button variant="outlined" onClick={handleBack}>
							Revenir aux étapes
						</Button>
						{isDirectNextStep &&
							<Button
								variant="contained"
								onClick={() => dispatch(showProductionStepExecution(ulteriorStep.objectId))}
							>
								Passer à l'étape suivante
							</Button>
						}
					</div>
				}
				leftAction={
					isUlteriorStepNotStarted &&
					<Button variant="outlined" onClick={handleStepBack}>
						Retour
					</Button>
				}
				justifyBetween={isUlteriorStepNotStarted}
			/>
		</>
	)

}

export default ProductionStepExecutionDone